import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentLayout } from 'components/Layouts'
import { useEasyPaySummary } from 'queries/premiums'
import { EasyPayNotEnrolledCard } from 'containers/Premiums/components/EasyPay/summary/EasyPayNotEnrolledCard'
import { Divider } from 'components/Divider/Divider'
import { EasyPayEnrolledCard } from 'containers/Premiums/components/EasyPay/summary/EasyPayEnrolledCard'
import { EasyPayBankAccountCard } from 'containers/Premiums/components/EasyPay/summary/EasyPayBankAccountCard'
import { Redirect } from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'
import { Alert } from '@cmsgov/ds-medicare-gov'
import classNames from 'classnames'
import { useElmoStatus } from 'queries/status'
import { BackToPremiumsLink } from 'containers/Premiums/components/BackToPremiums'
import { BackToPremiumsBreadcrumbFeatureFlagged } from 'containers/Premiums/components/BackToPremiumsBreadcrumb'
import { Component } from '@cms-max/medicare-api-sdk'
import { useFlags } from 'launchdarkly-react-client-sdk'

export const EasyPaySummaryPage: FC = () => {
  const { t } = useTranslation('easyPaySummary')

  const { data, isLoading, isError: isSummaryError } = useEasyPaySummary()
  const { isError: elmoError } = useElmoStatus()

  const flags = useFlags()

  if (data?.isEligible === false) {
    return <Redirect to={{
      pathname: '/premiums/easy-pay/error',
      search: `?code=ineligible`
    }} />
  }

  if (elmoError) {
    return <Redirect to={{
      pathname: '/premiums/easy-pay/error',
      search: `?code=unavailable`
    }} />
  }

  if (isSummaryError) {
    return (
      <ContentLayout
        className="ds-u-padding-bottom--7"
        heading={t('heading')}
        nav={<BackToPremiumsBreadcrumbFeatureFlagged />}
        width="wide"
        printComponent={Component.EasyPay}
      >
        <div className="ds-l-container">
          <Alert variation='error' heading={t('errorAlert.heading')} className='ds-u-measure--wide'>{t('errorAlert.content')}</Alert>

          <div className="ds-u-margin-top--3">
            <BackToPremiumsLink />
          </div>
        </div>
      </ContentLayout>
    )
  }

  return (
    <ContentLayout
      className="ds-u-padding-bottom--7"
      heading={t('heading')}
      nav={<BackToPremiumsBreadcrumbFeatureFlagged />}
      description={t('description')}
      width="wide"
      withPrintButton
      printComponent={Component.EasyPay}
    >

      <div className="ds-l-container">
        {isLoading && (
          <Alert variation='warn' heading={t('loadingAlert.heading')} className='ds-u-measure--wide' style={{ marginTop: '-16px' }}>
            {t('loadingAlert.content')}
          </Alert>
        )}

        <Divider className={classNames('my-u-my--20', {
          'ds-u-margin-top--3': isLoading
        })} />

        <div className="ds-l-row">
          <div className="ds-l-md-col--12 ds-l-lg-col--4">
            <h2 className="ds-text-heading--xl ds-u-margin-bottom--2">
              {isLoading ? <Skeleton /> : t('easyPayStatus.heading')}
            </h2>
            <p>{isLoading ? <Skeleton /> : t('easyPayStatus.description')}</p>
          </div>
          <div className="ds-l-md-col--12 ds-l-lg-col--8">
           {isLoading
             ? <Skeleton height={100} />
             : (
            <>
              {flags?.bicOutage202307 && <BicOutageAlert />}
              {data?.isEnrolled
                ? (
                <EasyPayEnrolledCard
                  status={data.status?.toLowerCase() as 'active' | 'pending'}
                  disableEdit={flags?.bicOutage202307}
                />
                  )
                : (
                <EasyPayNotEnrolledCard disableEdit={flags?.bicOutage202307} />
                  )}
            </>
               )}
          </div>
        </div>

        {(isLoading || data?.isEnrolled) && (
         <>
          <Divider className="my-u-my--20" />

          <div className="ds-l-row">
            <div className="ds-l-md-col--12 ds-l-lg-col--4">
              <h2 className="ds-text-heading--xl ds-u-margin-bottom--2">
                {isLoading ? <Skeleton /> : t('bankInfo.heading')}
              </h2>
              <p>{isLoading ? <Skeleton /> : t('bankInfo.description')}</p>
            </div>
            <div className="ds-l-md-col--12 ds-l-lg-col--8">
              {isLoading
                ? <Skeleton height={100} />
                : (
                <>
                {data?.accountNumberMasked
                  ? (
                <EasyPayBankAccountCard
                  accountNumber={data.accountNumberMasked}
                  disableEdit={flags?.bicOutage202307}
                />
                    )
                  : null}
                </>
                  )}
            </div>
          </div>
        </>
        )}
      </div>
    </ContentLayout>
  )
}

function BicOutageAlert () {
  const { t } = useTranslation('easyPaySummary')

  return (
    <Alert variation='warn' className='ds-u-measure--wide ds-u-margin-bottom--3'>
      {t('bicOutageAlert.content')}
    </Alert>
  )
}
